import {
    ref,
    onMounted,
    onUnmounted,
    watch,
    computed,
    unref
} from 'vue'

import initEcho from '@/services/echo'
import http from '@/api/http'
import { useToast } from '@/composables/useToast.js'

// ==================================================
// ETAT GLOBAL PARTAGE
// ==================================================

const notify = ref([])
const unreadCount = ref(0)
const loading = ref(false)

let listeningChannel = null

/**
 * Filet de sécurité : les notifications sont aussi rechargées périodiquement (onglet visible uniquement),
 * pour que chaque utilisateur les reçoive même si le serveur temps réel est absent ou injoignable.
 */
const POLL_INTERVAL_MS = 60000
let pollTimer = null

// ==================================================
// COMPOSABLE
// ==================================================

export function useNotifications(userId) {

    const channelName = computed(() => {

        const id = unref(userId)

        return id
            ? `App.Models.User.${id}`
            : null
    })

    // ==================================================
    // CHARGER LES NOTIFICATIONS
    // ==================================================

    const loadNotifications = async () => {

        const currentUserId = unref(userId)

        if (!currentUserId) {
            return
        }

        loading.value = true

        try {

            const response = await http.get('notifications')

            notify.value =
                response.data.notifications || []

            unreadCount.value =
                response.data.unread_count || 0

        } catch (error) {

            // Erreur silencieuse

        } finally {

            loading.value = false
        }
    }

    // ==================================================
    // RAFRAICHIR LES NOTIFICATIONS
    // ==================================================

    const refreshNotifications = async () => {

        await loadNotifications()
    }

    // ==================================================
    // ECOUTE REALTIME
    // ==================================================

    const listen = () => {

        const currentUserId = unref(userId)

        if (!currentUserId) {
            return
        }

        const channel = channelName.value

        if (!channel) {
            return
        }

        if (listeningChannel === channel) {
            return
        }

        try {

            const echo = initEcho()

            if (!echo) {
                startPolling()
                return
            }

            const privateChannel = echo.private(channel)

            privateChannel.notification((notification) => {

                if (notify.value.some((item) => item.id === notification.id)) {
                    return
                }

                notify.value.unshift({
                    ...notification,
                    read_at: null,
                    created_at: new Date().toISOString()
                })

                unreadCount.value++

                useToast().info(notification.message || 'Nouvelle notification.')

            })

            listeningChannel = channel
            startPolling()

        } catch (error) {

            // Erreur silencieuse

        }
    }

    // ==================================================
    // RAFRAICHISSEMENT PERIODIQUE (SANS TEMPS REEL)
    // ==================================================

    const startPolling = () => {

        if (pollTimer) {
            return
        }

        pollTimer = setInterval(() => {

            if (document.visibilityState === 'visible' && unref(userId)) {
                loadNotifications()
            }

        }, POLL_INTERVAL_MS)
    }

    const stopPolling = () => {

        clearInterval(pollTimer)
        pollTimer = null
    }

    // ==================================================
    // ARRETER L'ECOUTE
    // ==================================================

    const stopListening = () => {

        stopPolling()

        // Canal réellement écouté (celui de l'utilisateur précédent lors d'une déconnexion / changement de compte).
        const channel = listeningChannel

        listeningChannel = null

        if (!channel) {
            return
        }

        try {

            const echo = initEcho()

            echo?.leave(channel)

        } catch (error) {

            // Erreur silencieuse

        }
    }

    // ==================================================
    // MOUNTED
    // ==================================================

    onMounted(async () => {

        await loadNotifications()

        listen()
    })

    // ==================================================
    // CHANGEMENT UTILISATEUR
    // ==================================================

    watch(
        userId,
        async (newUserId, oldUserId) => {

            if (
                oldUserId &&
                oldUserId !== newUserId
            ) {
                stopListening()

                // Ne pas afficher les notifications du compte précédent.
                notify.value = []
                unreadCount.value = 0
            }

            if (newUserId) {

                await loadNotifications()

                listen()
            }
        }
    )

    // ==================================================
    // UNMOUNTED
    // ==================================================

    onUnmounted(() => {

        // Ne pas quitter le channel ici.
        // Le header peut toujours utiliser Echo.
    })

    return {
        notify,
        unreadCount,
        loading,
        loadNotifications,
        refreshNotifications,
        listen,
        stopListening
    }
}